/**
 * Array TRANSFORM helper. The array GUARDS (`isNonEmptyArray`, `hasNonEmptyArrayAt`) live in
 * `@federa/const/array-guards`, not here, so `utils/` leaves can reach them: a utils->utils value import is banned.
 */

/** True when the item's key was already seen (so it should be skipped). Items whose key is undefined are never duplicates. */
function isDuplicateKey<R>(item: R, keyFn: (result: R) => string | undefined, seen: Set<string>): boolean {
	const key = keyFn(item);
	if (key === undefined) return false;
	if (seen.has(key)) return true;
	seen.add(key);
	return false;
}

/** Transform array items, drop nulls, and optionally dedupe by keyFn: duplicate keys are skipped (first wins), and items whose keyFn returns undefined are always included. */
export function guardedArrayTransform<T, R>(
	raw: unknown,
	transformFn: (item: T, index: number) => R | null,
	keyFn?: (result: R) => string | undefined,
): R[] {
	if (!Array.isArray(raw)) return [];
	const results: R[] = [];
	const seen = keyFn ? new Set<string>() : undefined;
	for (let i = 0; i < raw.length; i++) {
		const transformed = transformFn(raw[i] as T, i);
		if (transformed == null) continue;
		if (seen && keyFn && isDuplicateKey(transformed, keyFn, seen)) continue;
		results.push(transformed);
	}
	return results;
}
